import React, { useState } from "react";
import { Breadcrumb } from "../EmployeeProfile/components";
import { EmployeeHeader } from "../EmployeeProfile/components";
import { EscalationSection } from "./components/EscalationSection";
import { SurveyDetails } from "./components/SurveyDetails";
import { ExitSurvey } from "./components/ExitSurvey";
import { FollowUpActions } from "./components/FollowUpActions";
import { FeedTimeline } from "./components/FeedTimeline";
import { ForwardEscalationDialog } from "./components/ForwardEscalationDialog";
import { SetReminderDialog } from "./components/SetReminderDialog";
import EscalationLogFollowUp from "../EscalationLogFollowUp";
import { BreadcrumbItem, IconName } from "../../types/navigation";
import { SurveyStatus } from "../../types";
import { useParams } from "react-router-dom";
import { fetchEmployee } from "modules/Retention/services/retention";
import { fetchEmployeeEscalationTicket, fetchFollowUpActions } from "modules/Retention/services/retention";
import endpoints from "modules/Retention/constants/endpoints";
import { useQueryClient } from "@tanstack/react-query";
import RETENTION_ROUTES, { getEmployeeProfileRoute } from "modules/Retention/constants/routes";
import { mapFollowUpActionsToFeedItems } from "modules/Retention/utils/followup";
import useUser from "hooks/userHooks";

export default function EscalationWithAI() {
    const { id, escalationId, surveyId } = useParams();
    const { user } = useUser();

    const employeeId = Number(id);
    const queryClient = useQueryClient();
    const [isLogFollowUpOpen, setIsLogFollowUpOpen] = useState(false);
    const [isForwardEscalationOpen, setIsForwardEscalationOpen] = useState(false);
    const [isSetReminderOpen, setIsSetReminderOpen] = useState(false);

    const [isLoadingEmployee, setIsLoadingEmployee] = useState(true);
    const [isLoadingEscalationTicket, setIsLoadingEscalationTicket] = useState(true);
    const [isLoadingFollowUpActions, setIsLoadingFollowUpActions] = useState(true);

    const [employeeError, setEmployeeError] = useState<any>(null);
    const [escalationError, setEscalationError] = useState<any>(null);
    const [setFollowUpError] = useState<any>(null);

    const [data, setData] = useState<any>(null);
    const [escalationTicketData, setEscalationTicketData] = useState<any>(null);
    const [followUpActionsData, setFollowUpActionsData] = useState<any>(null);

    const [isExitSurvey, setIsExitSurvey] = useState(false);

    const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([]);
    const [escalation, setEscalation] = useState<any>(null);
    const [survey, setSurvey] = useState<any>(null);
    const [feedItems, setFeedItems] = useState<any>([]);

    // Effect to fetch employee data
    React.useEffect(() => {
        const fetchEmployeeData = async () => {
            try {
                setIsLoadingEmployee(true);
                const res = await fetchEmployee({ employee_id: employeeId });
                setData(res?.result?.data);
            } catch (err) {
                setEmployeeError(err);
            } finally {
                setIsLoadingEmployee(false);
            }
        };

        fetchEmployeeData();
    }, [employeeId]);

    // Effect to fetch escalation ticket data
    React.useEffect(() => {
        const fetchEscalationTicketData = async () => {
            try {
                setIsLoadingEscalationTicket(true);
                const res = await fetchEmployeeEscalationTicket({ 
                    employee_id: employeeId, 
                    escalation_id: escalationId, 
                    survey_id: surveyId 
                });
                setEscalationTicketData(res?.result?.escalation_ticket);
                setIsExitSurvey(res?.result?.escalation_ticket?.survey_title?.toLowerCase().includes('exit') || false);
            } catch (err) {
                setEscalationError(err);
            } finally {
                setIsLoadingEscalationTicket(false);
            }
        };

        fetchEscalationTicketData();
    }, [employeeId, escalationId, surveyId]);

    // Effect to fetch follow-up actions data
    React.useEffect(() => {
        const fetchFollowUpActionsData = async () => {
            try {
                setIsLoadingFollowUpActions(true);
                const res = await fetchFollowUpActions({ escalation_id: escalationId });
                setFollowUpActionsData(res?.result?.follow_up_actions || []);
            } catch (err) {
                setFollowUpError(err);
            } finally {
                setIsLoadingFollowUpActions(false);
            }
        };

        fetchFollowUpActionsData();
    }, [escalationId]);

    // Effect to set breadcrumb items
    React.useEffect(() => {
        const newBreadcrumbItems: BreadcrumbItem[] = [
            { icon: IconName.FEATHER_HOME, label: "Homepage", href: RETENTION_ROUTES.HOME },
            { icon: IconName.FEATHER_LIST_CHECKS, label: "Task Center", href: RETENTION_ROUTES.TASK_CENTER },
            { icon: IconName.FEATHER_USER, label: `${data?.profile?.first_name || ''} ${data?.profile?.last_name || ''}`, href: getEmployeeProfileRoute(user?.agency_id).replace(":id", employeeId.toString()) },
            { icon: IconName.FEATHER_FILE_TEXT, label: escalationTicketData?.survey_title || "Survey" },
        ];
        setBreadcrumbItems(newBreadcrumbItems);
    }, [data, escalationTicketData, user?.agency_id, employeeId]);

    // Effect to set escalation data
    React.useEffect(() => {
        const newEscalation = {
            id: escalationTicketData?.escalation_id?.toString() || "",
            surveyId: escalationTicketData?.survey_id?.toString() || "",
            surveyTitle: escalationTicketData?.survey_title || "",
            createdAt: escalationTicketData?.escalation_created_at || "",
            level: escalationTicketData?.escalation_level, // This might need to be calculated based on escalation_reason
            reason: escalationTicketData?.escalation_reason || "",
            status: escalationTicketData?.status,
            aiSuggestions: [
                "Schedule 1:1 to discuss: Overtime compensation policy, Career development plan, Recognition program participation.",
                "Address work-life balance concerns and establish regular check-ins."
            ]
        };
        setEscalation(newEscalation);
    }, [escalationTicketData]);

    // Effect to set survey data
    React.useEffect(() => {
        const newSurvey = {
            id: escalationTicketData?.survey_id?.toString() || "",
            title: escalationTicketData?.survey_title || "",
            date: escalationTicketData?.escalation_created_at ? 
                new Date(escalationTicketData.escalation_created_at).toLocaleDateString() : "",
            questions: escalationTicketData?.survey_questions ? 
                Object.entries(escalationTicketData.survey_questions).map(([id, question]: [string, any]) => ({
                    id,
                    type: question.type,
                    question: question.question,
                    answer: question.response,
                    score: question.response,
                    status: question.sentiment === "Positive" ? SurveyStatus.HIGH : 
                            question.sentiment === "Negative" ? SurveyStatus.LOW : 
                            question.sentiment === "Neutral" ? SurveyStatus.MEDIUM : null
                })) : [],
            openEndedResponse: "", // Not available in current API response
            aiSummary: "", // Not available in current API response
            keyConcerns: [] // Not available in current API response
        };
        setSurvey(newSurvey);
    }, [escalationTicketData]);

    // Effect to set feed items
    React.useEffect(() => {
        const newFeedItems = mapFollowUpActionsToFeedItems(followUpActionsData || []);
        setFeedItems(newFeedItems);
    }, [followUpActionsData]);

    const handleLogFollowUp = () => {
        setIsLogFollowUpOpen(true);
    };

    const handleForwardEscalation = () => {
        setIsForwardEscalationOpen(true);
    };

    const handleSetReminder = () => {
        setIsSetReminderOpen(true);
    };

    const handleForwardEscalationSubmit = (data: { forwardTo: any; additionalContext: string }) => {
        // Refetch follow-up actions to show the new entry
        queryClient.invalidateQueries({ 
            queryKey: [endpoints.followUpActions, escalationId] 
        });
    };

    const handleSetReminderSubmit = (data: { reminderDateTime: string; reminderEscalationId: number }) => {
        // Refetch escalation data to show the new reminder
        queryClient.invalidateQueries({ 
            queryKey: [endpoints.employeeEscalationTicket, employeeId, escalationId, surveyId] 
        });
    };

    // Show error state if any critical query fails
    if (employeeError || escalationError) {
        return (
            <div className="flex h-full w-full flex-col items-center justify-center bg-default-background">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Data</h2>
                    <p className="text-gray-600">
                        {employeeError ? "Failed to load employee information." : ""}
                        {escalationError ? "Failed to load escalation details." : ""}
                    </p>
                </div>
            </div>
        );
    }

    // Show loading state while fetching critical data
    if (isLoadingEmployee || isLoadingEscalationTicket) {
        return (
            <div className="flex h-full w-full flex-col items-start bg-default-background pb-12">
                {/* Breadcrumb Skeleton */}
                <div className="flex h-16 w-full items-center justify-between border-b border-solid border-neutral-border px-4 py-3 md:px-8 lg:px-12 mb-4">
                    <div className="flex items-center gap-2">
                        {[1, 2, 3, 4].map((item) => (
                            <div key={item} className="flex items-center gap-2">
                                <div className="h-4 w-4 bg-neutral-200 rounded animate-pulse" />
                                <div className="h-4 w-20 bg-neutral-200 rounded animate-pulse" />
                                {item < 4 && <div className="h-4 w-4 bg-neutral-200 rounded animate-pulse" />}
                            </div>
                        ))}
                    </div>
                </div>
                
                <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-y-auto">
                    <div className="flex w-full flex-col items-start gap-4 px-4 pb-8 md:gap-6 md:px-8 lg:max-w-[1280px] lg:px-12">
                        {/* Employee Header Skeleton */}
                        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background px-4 py-4">
                            <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-full bg-neutral-200 animate-pulse" />
                                    <div className="flex flex-col gap-1">
                                        <div className="h-5 w-32 bg-neutral-200 rounded animate-pulse" />
                                        <div className="h-4 w-24 bg-neutral-200 rounded animate-pulse" />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="h-8 w-20 bg-neutral-200 rounded-full animate-pulse" />
                                    <div className="h-8 w-24 bg-neutral-200 rounded-full animate-pulse" />
                                </div>
                            </div>
                        </div>

                        {/* Escalation Section Skeleton */}
                        <div className="flex w-full flex-col items-start gap-4 rounded-lg bg-error-50 px-4 py-4">
                            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="h-6 w-48 bg-neutral-200 rounded animate-pulse" />
                                <div className="h-6 w-24 bg-neutral-200 rounded-full animate-pulse" />
                            </div>
                            <div className="flex flex-col items-start gap-1">
                                <div className="h-4 w-32 bg-neutral-200 rounded animate-pulse" />
                            </div>
                        </div>

                        {/* Survey Details Skeleton */}
                        <div className="flex w-full flex-col items-start justify-end gap-1 rounded-lg border border-solid border-neutral-border bg-default-background">
                            <div className="flex w-full items-center justify-between rounded-lg px-4 py-2">
                                <div className="h-5 w-32 bg-neutral-200 rounded animate-pulse" />
                                <div className="h-4 w-4 bg-neutral-200 rounded animate-pulse" />
                            </div>
                            <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-2 border-t border-solid border-neutral-border bg-neutral-50 px-4 py-4">
                                <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:justify-between">
                                    <div className="flex flex-col items-start gap-2">
                                        <div className="h-6 w-48 bg-neutral-200 rounded animate-pulse" />
                                    </div>
                                </div>
                                <div className="flex w-full flex-col items-start gap-1 rounded-md bg-white px-2 py-2 shadow-sm">
                                    {[1, 2, 3].map((item) => (
                                        <div key={item} className="flex w-full flex-col items-start border-b border-solid border-neutral-border px-1 py-1 last:border-b-0">
                                            <div className="w-full flex flex-col gap-2 justify-between sm:flex-row sm:items-start">
                                                <div className="w-full flex-1 h-4 bg-neutral-200 rounded animate-pulse" />
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <div className="h-6 w-20 bg-neutral-200 rounded animate-pulse" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Follow-up Actions Section Skeleton */}
                        <div className="flex w-full flex-col items-start gap-4">
                            <div className="h-6 w-32 bg-neutral-200 rounded animate-pulse" />
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                                <div className="h-10 w-32 bg-neutral-200 rounded animate-pulse" />
                                <div className="h-10 w-40 bg-neutral-200 rounded animate-pulse" />
                            </div>
                            
                            {/* Feed Timeline Skeleton */}
                            <div className="flex w-full flex-col items-start gap-4 px-2 md:max-w-[768px] md:px-6">
                                <div className="flex w-full flex-col items-start">
                                    {[1, 2, 3].map((item) => (
                                        <div key={item} className="flex w-full items-start gap-3 md:gap-4">
                                            <div className="flex flex-col items-center self-stretch">
                                                <div className="flex flex-col items-start gap-1">
                                                    <div className="h-8 w-8 rounded-full bg-neutral-200 animate-pulse" />
                                                </div>
                                                {item < 3 && (
                                                    <div className="flex w-0.5 grow shrink-0 basis-0 flex-col items-start gap-2 bg-neutral-border" />
                                                )}
                                            </div>
                                            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 pt-1 pb-7">
                                                <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                                                    <div className="flex grow shrink-0 basis-0 flex-wrap items-start gap-1">
                                                        <div className="h-4 w-32 bg-neutral-200 rounded animate-pulse" />
                                                        <div className="h-4 w-48 bg-neutral-200 rounded animate-pulse" />
                                                    </div>
                                                    <div className="h-4 w-24 bg-neutral-200 rounded animate-pulse" />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col items-start bg-default-background pb-12">
            <Breadcrumb
                isLoading={isLoadingEmployee || !data?.profile?.first_name}
                items={breadcrumbItems}
            />
            <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-y-auto">
                <div className="flex w-full flex-col items-start gap-4 px-4 pb-8 md:gap-6 md:px-8 lg:max-w-[1280px] lg:px-12">
                    <EmployeeHeader
                        employeeId={employeeId}
                    />
                    {isExitSurvey ? (
                        <ExitSurvey />
                    ) : (
                        <>
                            <EscalationSection 
                                escalation={escalation} 
                                isLoading={isLoadingEscalationTicket}
                            />
                            <SurveyDetails 
                                survey={survey} 
                                isLoading={isLoadingEscalationTicket}
                            />
                        </>
                    )}
                    <div className="flex w-full flex-col items-start gap-4">
                        {isLoadingFollowUpActions ? (
                            <>
                                <div className="h-6 w-32 bg-neutral-200 rounded animate-pulse" />
                                <FollowUpActions
                                    onLogFollowUp={handleLogFollowUp}
                                    onLearnMore={() => { }}
                                    onForwardEscalation={handleForwardEscalation}
                                    onSetReminder={handleSetReminder}
                                    isLoading={isLoadingFollowUpActions}
                                    employeeId={employeeId}
                                />
                                <FeedTimeline 
                                    items={feedItems} 
                                    isLoading={isLoadingFollowUpActions}
                                />
                            </>
                        ) : (
                            <>
                                <span className="text-section-header font-section-header text-default-font">
                                    {isExitSurvey ? "Exit Survey Log" : "Follow-up Actions"}
                                </span>
                                <FollowUpActions
                                    onLogFollowUp={handleLogFollowUp}
                                    onLearnMore={() => { }}
                                    onForwardEscalation={handleForwardEscalation}
                                    onSetReminder={handleSetReminder}
                                    isLoading={isLoadingFollowUpActions}
                                    employeeId={employeeId}
                                />
                                <FeedTimeline 
                                    items={feedItems} 
                                    isLoading={isLoadingFollowUpActions}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Escalation Log Follow-up Slideout */}
            <EscalationLogFollowUp 
                hidden={!isLogFollowUpOpen}
                open={isLogFollowUpOpen}
                onOpenChange={setIsLogFollowUpOpen}
                escalationId={escalationId}
                employeeId={employeeId}
                isLoading={isLoadingFollowUpActions}
                employee={data}
                onSubmit={() => {
                    // Refetch follow-up actions to show the new entry
                    queryClient.invalidateQueries({ 
                        queryKey: [endpoints.followUpActions, escalationId] 
                    });
                }}
            />

            {/* Forward Escalation Dialog */}
            <ForwardEscalationDialog
                open={isForwardEscalationOpen}
                onOpenChange={setIsForwardEscalationOpen}
                onSubmit={handleForwardEscalationSubmit}
                escalationId={Number(escalationId)}
                employeeId={employeeId}
            />

            {/* Set Reminder Dialog */}
            <SetReminderDialog
                open={isSetReminderOpen}
                onOpenChange={setIsSetReminderOpen}
                onSubmit={handleSetReminderSubmit}
                escalationId={Number(escalationId)}
                employeeId={employeeId}
            />
        </div>
    );
}